import React, { useRef, useState, useEffect } from 'react';
import { saveAs } from 'file-saver';
import './WorkOrderFilesTab.css';

function WorkOrderFilesTab({
  folders = [],
  setFolders,
  comments = [],
  handleCreateFolder,
  handleRenameFolder,
  handleDeleteFolder,
  handleDragMoveFolder,
  handleDeleteComment,
  handleReplySubmit,
  currentUser,
  isAuthor,
  isAssignee,
  isCc = false,
  workOrder
}) {
  const isLocked = ['stop', 'done', 'completed', 'cancel', 'canceled'].includes(workOrder?.status);
  const [newFolderName, setNewFolderName] = useState('');
  const [replyTargetId, setReplyTargetId] = useState(null);
  const [replyFile, setReplyFile] = useState(null);
  const replyFileInputRef = useRef(null);

  // === 모달 상태 관리 ===
  // 1. 폴더 선택 이동 모달
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [targetFileId, setTargetFileId] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);

  // 2. 폴더명 변경 모달
  const [editFolderModalVisible, setEditFolderModalVisible] = useState(false);
  const [selectedEditFolder, setSelectedEditFolder] = useState('');
  const [newEditFolderName, setNewEditFolderName] = useState('');

  // 3. 폴더 삭제 모달
  const [deleteFolderModalVisible, setDeleteFolderModalVisible] = useState(false);
  const [selectedDeleteFolder, setSelectedDeleteFolder] = useState('');

  // === 드래그 앤 드롭 상태 관리 ===
  const [draggedCommentId, setDraggedCommentId] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const scrollContainerRef = useRef(null);
  const scrollIntervalRef = useRef(null);

  // === 아코디언 상태 관리 (PMS 동일) ===
  const [openFolders, setOpenFolders] = useState({});

  useEffect(() => {
    if (folders && folders.length > 0) {
      setOpenFolders(prev => {
        const next = { ...prev };
        folders.forEach(f => {
          const key = typeof f === 'string' ? f : (f.folder_name || String(f));
          if (next[key] === undefined) {
            next[key] = true; // 기본값 펼침
          }
        });
        return next;
      });
    }
  }, [folders]);

  const toggleFolder = (folderName) => {
    setOpenFolders(prev => {
      const current = prev[folderName] !== false; // undefined or true => true
      return {
        ...prev,
        [folderName]: !current
      };
    });
  };

  const handleExpandAll = () => {
    const allOpen = {};
    folders.forEach(f => {
      const key = typeof f === 'string' ? f : (f.folder_name || String(f));
      allOpen[key] = true;
    });
    setOpenFolders(allOpen);
  };

  const handleCollapseAll = () => {
    const allClosed = {};
    folders.forEach(f => {
      const key = typeof f === 'string' ? f : (f.folder_name || String(f));
      allClosed[key] = false;
    });
    setOpenFolders(allClosed);
  };

  const getFileIconClass = (fileName) => {
    if (!fileName) return 'fas fa-file-alt text-secondary';
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return 'fas fa-file-pdf text-danger';
      case 'doc': case 'docx': case 'rtf': return 'fas fa-file-word text-primary';
      case 'xls': case 'xlsx': case 'csv': return 'fas fa-file-excel text-success';
      case 'ppt': case 'pptx': case 'pps': return 'fas fa-file-powerpoint text-warning';
      case 'hwp': case 'hwpx': return 'fas fa-file-alt text-info';
      case 'jpg': case 'jpeg': case 'png': case 'gif': case 'svg': case 'webp': case 'bmp': case 'tiff': case 'ico': return 'fas fa-file-image text-success';
      case 'zip': case 'rar': case '7z': case 'tar': case 'gz': case 'alz': case 'egg': return 'fas fa-file-archive text-secondary';
      case 'mp4': case 'avi': case 'mkv': case 'mov': case 'wmv': case 'flv': case 'webm': return 'fas fa-file-video text-danger';
      case 'mp3': case 'wav': case 'ogg': case 'flac': case 'm4a': return 'fas fa-file-audio text-warning';
      case 'js': case 'json': case 'html': case 'css': case 'xml': case 'py': case 'java': case 'c': case 'cpp': case 'sql': case 'log': case 'sh': case 'php': return 'fas fa-file-code text-dark';
      case 'txt': case 'md': return 'fas fa-file-alt text-secondary';
      default: return 'fas fa-file text-muted';
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const pad = n => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const stripUniqueKey = (name) => {
    if (!name) return name;
    return name.replace(/_[0-9]{8}F[0-9]{6}(?=\.[^.]+$|$)/g, '').replace(/^[0-9]+_/, '');
  };

  const handleDownloadFile = async (fileObj, fallbackName = 'download') => {
    if (!fileObj && !fallbackName) {
      alert('다운로드할 파일 정보가 없습니다.');
      return;
    }
    const path = typeof fileObj === 'string'
      ? fileObj
      : (fileObj?.file_path || fileObj?.path || (fileObj?.file_name ? `/uploads/techni/workOrder/${fileObj.file_name}` : null));

    const fileName = typeof fileObj === 'object'
      ? (fileObj?.file_name || fileObj?.originalName || fallbackName)
      : fallbackName;

    if (!path) {
      alert('다운로드할 파일 경로가 존재하지 않습니다.');
      return;
    }

    try {
      const response = await fetch(path);
      const blob = await response.blob();
      saveAs(blob, stripUniqueKey(fileName));
    } catch (e) {
      console.error('다운로드 실패:', e);
      window.open(path, '_blank');
    }
  };

  const checkIsFile = (c) => {
    if (!c) return false;
    if (c.file || c.file_path || c.file_name) return true;
    if (c.files && c.files.length > 0) return true;
    return c.content && /\.(gif|jpg|jpeg|png|pdf|zip|doc|docx|xls|xlsx|ppt|pptx|txt|hwp|csv|ai|psd)$/i.test(c.content.trim());
  };

  // === 드래그 앤 드롭 이벤트 핸들러 ===
  const handleDragStart = (e, commentId) => {
    if (isCc) {
      e.preventDefault();
      return;
    }
    setDraggedCommentId(commentId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(commentId));
    setTimeout(() => {
      const el = document.getElementById(`file-node-${commentId}`);
      if (el) el.style.opacity = '0.4';
    }, 0);
  };

  const handleDragEnd = (e) => {
    if (e.currentTarget) e.currentTarget.style.opacity = '1';
    if (draggedCommentId) {
      const el = document.getElementById(`file-node-${draggedCommentId}`);
      if (el) el.style.opacity = '1';
    }
    document.querySelectorAll('[id^="file-node-"]').forEach(el => {
      el.style.opacity = '1';
    });
    setDraggedCommentId(null);
    setDragOverFolder(null);
    stopAutoScroll();
  };

  const handleFolderDragOver = (e, folderName) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverFolder(folderName);
    const container = scrollContainerRef.current;
    if (container) {
      const rect = container.getBoundingClientRect();
      const y = e.clientY;
      if (y < rect.top + 50) startAutoScroll(container, -5);
      else if (y > rect.bottom - 50) startAutoScroll(container, 5);
      else stopAutoScroll();
    }
  };

  const handleFolderDragLeave = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOverFolder(null);
  };

  const handleFolderDrop = async (e, folderName) => {
    e.preventDefault();
    setDragOverFolder(null);
    stopAutoScroll();
    let droppedId = draggedCommentId;
    if (!droppedId) {
      const droppedIdStr = e.dataTransfer.getData('text/plain');
      if (droppedIdStr) droppedId = isNaN(Number(droppedIdStr)) ? droppedIdStr : Number(droppedIdStr);
    }
    if (droppedId && handleDragMoveFolder) {
      const el = document.getElementById(`file-node-${droppedId}`);
      if (el) el.style.opacity = '1';
      await handleDragMoveFolder(droppedId, folderName);
    }
    setDraggedCommentId(null);
  };

  const startAutoScroll = (container, speed) => {
    stopAutoScroll();
    scrollIntervalRef.current = setInterval(() => {
      container.scrollTop += speed;
    }, 16);
  };

  const stopAutoScroll = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  // 모달 제어 핸들러
  const openFolderModal = (fileId) => {
    setTargetFileId(fileId);
    setSelectedFolder(null);
    setShowFolderModal(true);
  };

  const handleMoveFolderSubmit = async () => {
    if (selectedFolder === null || selectedFolder === undefined) {
      alert('이동할 폴더를 선택하세요.');
      return;
    }
    const targetFile = comments.find(c => String(c.id) === String(targetFileId));
    const currentFolder = targetFile ? (targetFile.folder_name || '') : '';
    if (currentFolder === selectedFolder) {
      alert('현재 위치와 동일한 폴더입니다.');
      return;
    }
    if (handleDragMoveFolder) {
      await handleDragMoveFolder(targetFileId, selectedFolder);
    }
    setShowFolderModal(false);
    setSelectedFolder(null);
  };

  const openEditFolderModal = (folder) => {
    setSelectedEditFolder(folder);
    setNewEditFolderName(folder);
    setEditFolderModalVisible(true);
  };

  const handleEditFolderSubmit = async () => {
    if (!newEditFolderName || !newEditFolderName.trim()) {
      alert('변경할 폴더명을 입력하세요.');
      return;
    }
    if (newEditFolderName.trim() === selectedEditFolder) {
      setEditFolderModalVisible(false);
      return;
    }
    if (handleRenameFolder) {
      await handleRenameFolder(selectedEditFolder, newEditFolderName.trim());
    }
    setEditFolderModalVisible(false);
  };

  const openDeleteFolderModal = (folder) => {
    setSelectedDeleteFolder(folder);
    setDeleteFolderModalVisible(true);
  };

  const handleDeleteFolderSubmit = async (deleteMode) => {
    if (handleDeleteFolder) {
      await handleDeleteFolder(selectedDeleteFolder, deleteMode);
    }
    setDeleteFolderModalVisible(false);
  };

  // === 답글 렌더링 (PMS 100% 동일) ===
  const renderReplies = (parentId, depth = 1) => {
    const childReplies = (comments || []).filter(c => c.parent_id === parentId).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    if (childReplies.length === 0) return null;

    return childReplies.map(reply => {
      const isReplyFile = checkIsFile(reply);
      const indentPx = 36 + (depth * 16);
      const displayName = stripUniqueKey(reply.file?.originalName || reply.file_name || reply.content);

      return (
        <div key={reply.id} className="d-flex flex-column">
          <div className="file-item-row d-flex align-items-center py-1 px-2 hover-bg-light rounded mb-1">
            <div style={{ width: `${indentPx}px`, minWidth: `${indentPx}px` }} />
            <i className="fas fa-level-up-alt fa-rotate-90 text-primary me-2 fs-8 opacity-50" />
            <i className={`${getFileIconClass(displayName)} fa-fw me-2 fs-6`} />

            {/* 좌측 밀착 자연스러운 흐름 배치 */}
            <div className="d-flex align-items-center flex-wrap">
              {isReplyFile ? (
                <>
                  <span
                    className="file-link-hover fs-7 me-2 transition-colors"
                    onClick={() => handleDownloadFile(reply.file || reply, displayName)}
                    title={`${displayName} (클릭하여 다운로드)`}
                  >
                    {displayName}
                  </span>
                  <span className="badge bg-light text-secondary border fs-8 me-3 px-2 py-1" style={{ fontWeight: '500' }}>
                    {Math.round((reply.file?.file_size || reply.file_size || 1024) / 1024)} KB
                  </span>
                </>
              ) : (
                <span className="text-dark fs-7 me-2" style={{ whiteSpace: 'pre-wrap' }}>{reply.content}</span>
              )}
              <span className="text-secondary d-flex align-items-center me-2" style={{ fontSize: '11px', fontWeight: '400' }}>
                <span className="opacity-75">{reply.user?.name || reply.author_name || '알 수 없음'} {reply.user?.org?.position || reply.user?.position || ''}</span>
                <span className="text-muted ms-2 d-flex align-items-center">
                  <span className="opacity-50 mx-1">•</span>
                  {formatDateTime(reply.created_at)}
                </span>
              </span>
            </div>

            {/* 우측 액션 버튼 */}
            <div className="d-flex align-items-center opacity-0 action-buttons transition-opacity ms-auto">
              {isReplyFile && (
                <button
                  type="button"
                  className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded"
                  style={{ background: 'transparent' }}
                  title="다운로드"
                  onClick={() => handleDownloadFile(reply.file || reply, displayName)}
                >
                  <i className="fas fa-download" />
                </button>
              )}
              {!isLocked && !isCc && (
                <button
                  type="button"
                  className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none ms-1 rounded"
                  style={{ background: 'transparent' }}
                  title="답글(파일첨부)"
                  onClick={() => setReplyTargetId(reply.id)}
                >
                  <i className="fas fa-plus" />
                </button>
              )}
              {!isLocked && !isCc && (isAuthor || (currentUser && currentUser.name === (reply.user?.name || reply.author_name))) && (
                <button
                  type="button"
                  className="btn btn-sm text-danger hover-bg-gray p-1 shadow-none ms-1 rounded"
                  style={{ background: 'transparent' }}
                  title="삭제"
                  onClick={() => handleDeleteComment && handleDeleteComment(reply.id)}
                >
                  <i className="fas fa-trash-alt" />
                </button>
              )}
            </div>
          </div>

          {replyTargetId === reply.id && (
            <div className="mt-1 mb-2 d-flex align-items-center gap-2 p-2 border-bottom" style={{ paddingLeft: `${indentPx + 24}px` }}>
              <div className="d-flex bg-white border flex-grow-1 align-items-center rounded" style={{ height: '30px' }}>
                <input
                  type="text"
                  className="form-control border-0 shadow-none h-100 flex-grow-1 rounded fs-8 px-2 bg-white"
                  placeholder={replyFile ? replyFile.name : '첨부할 파일을 선택하세요...'}
                  readOnly
                  onClick={() => replyFileInputRef.current?.click()}
                  style={{ cursor: 'pointer' }}
                />
                <input type="file" className="d-none" ref={replyFileInputRef} onChange={(e) => setReplyFile(e.target.files[0])} />
                <button
                  type="button"
                  className="btn btn-sm bg-white border-start rounded-0 text-primary h-100 px-3 d-flex align-items-center justify-content-center"
                  title="파일 첨부"
                  onClick={() => replyFileInputRef.current?.click()}
                >
                  <i className="fas fa-upload" />
                </button>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-primary rounded px-3 fw-bold shadow-sm"
                style={{ height: '30px', fontSize: '12px' }}
                onClick={() => {
                  if (handleReplySubmit) handleReplySubmit(reply.id, replyFile ? replyFile.name : '', replyFile);
                  setReplyFile(null);
                  setReplyTargetId(null);
                }}
              >
                저장
              </button>
              <button
                type="button"
                className="btn btn-sm btn-secondary rounded px-3 fw-bold shadow-sm"
                style={{ height: '30px', fontSize: '12px' }}
                onClick={() => { setReplyTargetId(null); setReplyFile(null); }}
              >
                취소
              </button>
            </div>
          )}

          {renderReplies(reply.id, depth + 1)}
        </div>
      );
    });
  };

  // === 루트 코멘트 렌더링 (PMS 100% 동일) ===
  const renderRootComment = (comment, isRootLevel = false) => {
    const isFile = checkIsFile(comment);
    const displayName = stripUniqueKey(comment.file?.originalName || comment.file_name || comment.content);
    const authorName = comment.user?.name || comment.author_name || '알 수 없음';
    const authorPos = comment.user?.org?.position || comment.user?.position || '';

    return (
      <div key={comment.id} id={`file-node-${comment.id}`} className="d-flex flex-column transition-opacity" style={{ transition: 'opacity 0.2s' }}>
        <div className="file-item-row d-flex align-items-center py-2 px-2 hover-bg-light rounded position-relative mb-1">
          {!isRootLevel && <div style={{ width: '24px', minWidth: '24px' }} />}

          {/* 그립 아이콘 (수정본): 기본 투명, 호버 시에만 옅게 등장 (참조자는 드래그 차단) */}
          <div
            className="text-muted d-flex align-items-center justify-content-center me-1 grip-icon transition-opacity"
            style={{ cursor: (!isCc && isFile) ? 'grab' : 'default', width: '16px', opacity: 0 }}
            draggable={!isCc && isFile}
            onDragStart={(!isCc && isFile) ? (e) => handleDragStart(e, comment.id) : undefined}
            onDragEnd={(!isCc && isFile) ? handleDragEnd : undefined}
            title={(!isCc && isFile) ? '드래그하여 다른 폴더로 이동' : ''}
          >
            <i className="fas fa-grip-lines fs-7 opacity-50" />
          </div>

          <i className={`${isFile ? getFileIconClass(stripUniqueKey(comment.file?.originalName || comment.content)) : getFileIconClass(comment.content)} fa-fw me-2 fs-6`} />

          {/* 좌측 밀착 자연스러운 흐름 배치 */}
          <div className="d-flex align-items-center flex-wrap">
            {isFile ? (
              <>
                <span
                  className="file-link-hover fs-7 me-2 transition-colors"
                  onClick={() => handleDownloadFile(comment.file || comment, displayName)}
                  title={`${displayName} (클릭하여 다운로드)`}
                >
                  {displayName}
                </span>
                <span className="badge bg-light text-secondary border fs-8 me-3 px-2 py-1" style={{ fontWeight: '500' }}>
                  {Math.round((comment.file?.file_size || comment.file_size || 1024) / 1024)} KB
                </span>
              </>
            ) : (
              <span className="text-dark fs-7 me-2" style={{ whiteSpace: 'pre-wrap' }}>{comment.content}</span>
            )}

            <span className="text-secondary opacity-75 me-2" style={{ fontSize: '11px', fontWeight: '400' }}>
              {authorName} {authorPos} <span className="opacity-50 mx-1">|</span> {formatDateTime(comment.created_at)}
            </span>
          </div>

          {/* 우측 끝 액션 버튼 */}
          <div className="d-flex align-items-center opacity-0 action-buttons transition-opacity ms-auto">
            {isFile && (
              <button
                type="button"
                className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded"
                style={{ background: 'transparent' }}
                title="다운로드"
                onClick={() => handleDownloadFile(comment.file || comment, displayName)}
              >
                <i className="fas fa-download" />
              </button>
            )}
            {!isLocked && !isCc && (
              <button
                type="button"
                className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none ms-1 rounded"
                style={{ background: 'transparent' }}
                title="답글(파일첨부)"
                onClick={() => setReplyTargetId(comment.id)}
              >
                <i className="fas fa-plus" />
              </button>
            )}
            {!isLocked && !isCc && (isAuthor || (currentUser && currentUser.name === authorName)) && (
              <button
                type="button"
                className="btn btn-sm text-danger hover-bg-gray p-1 shadow-none ms-1 rounded"
                style={{ background: 'transparent' }}
                title="삭제"
                onClick={() => handleDeleteComment && handleDeleteComment(comment.id)}
              >
                <i className="fas fa-trash-alt" />
              </button>
            )}
            {!isCc && isFile && (
              folders && folders.length === 0 ? (
                <span title="등록된 폴더가 존재하지 않습니다.">
                  <button type="button" className="btn btn-sm text-warning opacity-50 p-1 shadow-none ms-1 rounded" style={{ background: 'transparent' }} disabled>
                    <i className="far fa-folder" />
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="btn btn-sm text-warning hover-bg-gray p-1 shadow-none ms-1 rounded"
                  style={{ background: 'transparent' }}
                  title="폴더이동"
                  onClick={() => openFolderModal(comment.id)}
                >
                  <i className="far fa-folder" />
                </button>
              )
            )}
          </div>
        </div>

        {replyTargetId === comment.id && (
          <div className="mt-1 mb-2 d-flex align-items-center gap-2 p-2 border-bottom" style={{ paddingLeft: '48px' }}>
            <div className="d-flex bg-white border flex-grow-1 align-items-center rounded" style={{ height: '30px' }}>
              <input
                type="text"
                className="form-control border-0 shadow-none h-100 flex-grow-1 rounded fs-8 px-2 bg-white"
                placeholder={replyFile ? replyFile.name : '첨부할 파일을 선택하세요...'}
                readOnly
                onClick={() => replyFileInputRef.current?.click()}
                style={{ cursor: 'pointer' }}
              />
              <input type="file" className="d-none" ref={replyFileInputRef} onChange={(e) => setReplyFile(e.target.files[0])} />
              <button
                type="button"
                className="btn btn-sm bg-white border-start rounded-0 text-primary h-100 px-3 d-flex align-items-center justify-content-center"
                title="파일 첨부"
                onClick={() => replyFileInputRef.current?.click()}
              >
                <i className="fas fa-upload" />
              </button>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-primary rounded px-3 fw-bold shadow-sm"
              style={{ height: '30px', fontSize: '12px' }}
              onClick={() => {
                if (handleReplySubmit) handleReplySubmit(comment.id, replyFile ? replyFile.name : '', replyFile);
                setReplyFile(null);
                setReplyTargetId(null);
              }}
            >
              저장
            </button>
            <button
              type="button"
              className="btn btn-sm btn-secondary rounded px-3 fw-bold shadow-sm"
              style={{ height: '30px', fontSize: '12px' }}
              onClick={() => { setReplyTargetId(null); setReplyFile(null); }}
            >
              취소
            </button>
          </div>
        )}

        {renderReplies(comment.id, isRootLevel ? 0 : 1)}
      </div>
    );
  };

  const rootComments = (comments || []).filter(c => !c.parent_id);
  const fileRootComments = rootComments.filter(c => checkIsFile(c)).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const unassignedFiles = [];
  const filesByFolder = {};
  folders.forEach(f => {
    const key = typeof f === 'string' ? f : (f.folder_name || String(f));
    filesByFolder[key] = [];
  });

  fileRootComments.forEach(c => {
    const fName = c.folder_name;
    if (fName && filesByFolder[fName] !== undefined) {
      filesByFolder[fName].push(c);
    } else {
      unassignedFiles.push(c);
    }
  });

  const countDeepFiles = (rootArr) => {
    let total = 0;
    const countChildren = (parentId) => {
      const children = (comments || []).filter(c => c.parent_id === parentId);
      total += children.length;
      children.forEach(child => countChildren(child.id));
    };
    rootArr.forEach(c => {
      total += 1;
      countChildren(c.id);
    });
    return total;
  };

  const hasFilesInFolders = Object.values(filesByFolder).some(arr => arr && arr.length > 0);

  return (
    <div className="d-flex flex-column h-100 position-relative">
      {/* 상단 툴바: PMS 100% 동일 */}
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
        {/* 전체 펼치기 / 접기 버튼 (폴더 내 파일이 있을 때만 표출) */}
        {folders.length > 0 && hasFilesInFolders ? (
          <div className="d-flex gap-2" style={{ transform: 'translateY(12px)' }}>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary text-nowrap px-3 py-1 fs-8 fw-bold"
              onClick={handleExpandAll}
              title="모든 폴더 펼치기"
            >
              <i className="fas fa-angle-double-down me-2" />전체 펼치기
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary text-nowrap px-3 py-1 fs-8 fw-bold"
              onClick={handleCollapseAll}
              title="모든 폴더 접기"
            >
              <i className="fas fa-angle-double-up me-2" />전체 접기
            </button>
          </div>
        ) : (
          <div />
        )}

        {!isCc ? (
          <div className="d-flex border rounded bg-white shadow-sm align-items-center ps-3 pe-1 py-1" style={{ width: '100%', maxWidth: '300px' }}>
            <input
              type="text"
              className="form-control border-0 shadow-none p-0 fs-8 light-placeholder bg-transparent h-100"
              placeholder="새로운 폴더 이름 입력"
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && newFolderName.trim()) {
                  if (handleCreateFolder) handleCreateFolder(newFolderName.trim());
                  setNewFolderName('');
                }
              }}
            />
            <button
              type="button"
              className="btn btn-sm btn-primary rounded fw-bold px-3 ms-2 shadow-sm text-nowrap"
              onClick={() => {
                if (newFolderName.trim() && handleCreateFolder) {
                  handleCreateFolder(newFolderName.trim());
                  setNewFolderName('');
                }
              }}
            >
              추가
            </button>
          </div>
        ) : (
          <span className="text-muted fs-8">
            <i className="fas fa-lock me-1 text-secondary" />참조자는 폴더 및 파일을 조회/다운로드만 할 수 있습니다.
          </span>
        )}
      </div>

      {/* 퓨어 트리 뷰 영역 */}
      <div
        ref={scrollContainerRef}
        className="flex-grow-1 overflow-auto bg-white border rounded shadow-sm custom-scrollbar position-relative"
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverFolder(''); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverFolder(null); }}
        onDrop={(e) => handleFolderDrop(e, '')}
      >
        {dragOverFolder === '' && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 100, pointerEvents: 'none' }}
          >
            <div className="bg-primary text-white px-4 py-2 rounded-pill shadow-lg fw-bold fs-6">
              <i className="fas fa-reply fa-rotate-180 me-2" /> 최상위로 이동
            </div>
          </div>
        )}

        {(fileRootComments.length === 0 && folders.length === 0) ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5 mt-4 opacity-75">
            <h6 className="fw-bold text-dark mb-1">등록된 파일이 없습니다</h6>
            <p className="text-muted fs-8 mb-0 text-center">폴더를 생성하여 파일들을 정리해 보세요!</p>
          </div>
        ) : (
          <div className="pb-3 w-100">
            {/* 1. 폴더 목록 */}
            {folders.map((folderItem, idx) => {
              const folder = typeof folderItem === 'string' ? folderItem : (folderItem.folder_name || String(folderItem));
              const folderFiles = filesByFolder[folder] || [];
              const isOpen = openFolders[folder] !== false; // 기본값: 열림

              return (
                <div
                  key={idx}
                  className="w-100"
                  style={{
                    backgroundColor: dragOverFolder === folder ? '#eef2ff' : 'transparent',
                    transition: 'background-color 0.2s ease'
                  }}
                  onDragOver={(e) => { e.stopPropagation(); handleFolderDragOver(e, folder); }}
                  onDragLeave={handleFolderDragLeave}
                  onDrop={(e) => { e.stopPropagation(); handleFolderDrop(e, folder); }}
                >
                  {/* 폴더 헤더: 파일이 있을 때만 토글 동작, 없을 때는 흐리게(비활성화) 표출 */}
                  <div
                    className={`d-flex align-items-center py-2 px-2 folder-header border-bottom ${folderFiles.length > 0 ? 'hover-bg-light cursor-pointer' : ''}`}
                    onClick={() => {
                      if (folderFiles.length > 0) toggleFolder(folder);
                    }}
                  >
                    <div style={{ width: '20px', textAlign: 'center' }}>
                      <i
                        className={`fas fa-angle-${isOpen ? 'down' : 'right'} text-secondary fs-6 transition-transform`}
                        style={{ opacity: folderFiles.length === 0 ? 0.35 : 1 }}
                      />
                    </div>
                    <i
                      className="fas fa-folder text-warning mx-2 fs-5 transition-opacity"
                      style={{ color: '#fcc419', opacity: folderFiles.length === 0 ? 0.5 : 1 }}
                    />

                    {/* 폴더명 및 파일 갯수 뱃지 */}
                    <span className={`fs-7 fw-bold transition-opacity ${folderFiles.length === 0 ? 'text-secondary opacity-75' : 'text-dark'}`}>
                      {folder}
                    </span>
                    <span className="badge rounded-pill ms-2" style={{ backgroundColor: '#e9ecef', color: '#6c757d', fontSize: '11px', padding: '3px 7px' }}>
                      {countDeepFiles(folderFiles)}
                    </span>

                    {/* 우측 폴더 액션 버튼 */}
                    {!isCc && (
                      <div className="d-flex gap-1 folder-actions opacity-0 transition-opacity ms-auto" onClick={e => e.stopPropagation()}>
                        <button
                          type="button"
                          className="btn btn-sm text-secondary hover-bg-gray p-1 shadow-none rounded"
                          style={{ background: 'transparent' }}
                          title="폴더명 변경"
                          onClick={() => openEditFolderModal(folder)}
                        >
                          <i className="fas fa-edit" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm text-danger hover-bg-gray p-1 shadow-none rounded"
                          style={{ background: 'transparent' }}
                          title="폴더 삭제"
                          onClick={() => openDeleteFolderModal(folder)}
                        >
                          <i className="fas fa-trash-alt" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* 폴더 내부 파일 목록 (isOpen이 true일 때 표시) */}
                  {isOpen && folderFiles.length > 0 && (
                    <div className="w-100">
                      {folderFiles.map((comment) => renderRootComment(comment))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* 2. 소속 없는 최상위 파일들 */}
            {unassignedFiles.length > 0 && (
              <div className="w-100">
                {unassignedFiles.map((comment) => renderRootComment(comment, true))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ──────────────────────────────────────────────────────── */}
      {/* [모달 1] 이동할 폴더 선택 모달 (PMS 100% 동일) */}
      {/* ──────────────────────────────────────────────────────── */}
      {showFolderModal && (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: -1, backgroundColor: 'transparent' }} onClick={(e) => e.stopPropagation()} />
          <div className="modal-backdrop fade show" style={{ opacity: 0.5, position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: '#000', zIndex: 0 }} />

          <div className="modal-content shadow-lg border-0" style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '420px', borderRadius: '12px', overflow: 'hidden' }}>
            <div className="modal-header py-3 px-4 border-bottom d-flex justify-content-between align-items-center" style={{ backgroundColor: '#f1f3f5', borderColor: '#e9ecef' }}>
              <h6 className="modal-title fw-bolder text-dark d-flex align-items-center mb-0" style={{ fontSize: '1.05rem', letterSpacing: '-0.3px' }}>
                <i className="far fa-folder-open me-2" style={{ color: '#4c6ef5', fontSize: '1.2rem' }} />
                이동할 폴더 선택
              </h6>
              <button
                type="button"
                className="btn btn-light rounded-circle text-muted border-0 shadow-none d-flex align-items-center justify-content-center"
                onClick={() => setShowFolderModal(false)}
                style={{ width: '28px', height: '28px', backgroundColor: '#e9ecef' }}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="modal-body p-3 bg-white">
              {folders.length === 0 ? (
                <div className="text-center text-muted py-4 border rounded-3" style={{ backgroundColor: '#f8f9fa', borderColor: '#e5e7eb' }}>
                  <p className="mb-0 fs-7">이동할 폴더가 없습니다.</p>
                </div>
              ) : (
                <div className="border rounded-3 overflow-hidden shadow-sm" style={{ borderColor: '#e5e7eb' }}>
                  <div style={{ maxHeight: '180px', overflowY: 'auto' }}>
                    {/* 최상위로 이동 */}
                    <div
                      className="d-flex align-items-center px-3 py-2 position-relative border-bottom"
                      onClick={() => {
                        const targetFile = comments.find(c => String(c.id) === String(targetFileId));
                        if (targetFile && (targetFile.folder_name || '') === '') {
                          alert('현재 이미 최상위에 위치하고 있습니다.');
                          return;
                        }
                        setSelectedFolder('');
                      }}
                      style={{
                        borderColor: '#f1f3f5',
                        backgroundColor: selectedFolder === '' ? '#f0f4ff' : '#ffffff',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      <div className="fw-bold fs-7 d-flex align-items-center flex-grow-1">
                        <div
                          className="d-flex align-items-center justify-content-center me-3 shadow-sm"
                          style={{ width: '28px', height: '28px', borderRadius: '8px', backgroundColor: selectedFolder === '' ? '#4c6ef5' : '#f1f3f5', transition: 'all 0.2s ease' }}
                        >
                          <i className={`fas fa-reply ${selectedFolder === '' ? 'text-white' : 'text-secondary'}`} style={{ fontSize: '0.8rem' }} />
                        </div>
                        <span style={{ color: selectedFolder === '' ? '#4c6ef5' : '#495057' }}>최상위로 이동</span>
                      </div>
                      {selectedFolder === '' && (
                        <div className="ms-2">
                          <i className="fas fa-check" style={{ color: '#4c6ef5', fontSize: '0.9rem' }} />
                        </div>
                      )}
                    </div>

                    {/* 폴더 목록 */}
                    {folders.map((fItem, idx) => {
                      const f = typeof fItem === 'string' ? fItem : (fItem.folder_name || String(fItem));
                      return (
                        <div
                          className={`d-flex align-items-center px-3 py-2 position-relative ${idx !== folders.length - 1 ? 'border-bottom' : ''}`}
                          key={idx}
                          onClick={() => {
                            const targetFile = comments.find(c => String(c.id) === String(targetFileId));
                            if (targetFile && (targetFile.folder_name || '') === f) {
                              alert('현재 위치와 동일한 폴더입니다.');
                              return;
                            }
                            setSelectedFolder(f);
                          }}
                          style={{
                            borderColor: '#f1f3f5',
                            backgroundColor: selectedFolder === f ? '#f0f4ff' : '#ffffff',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                          }}
                        >
                          <div className="fw-bold fs-7 d-flex align-items-center flex-grow-1">
                            <div
                              className="d-flex align-items-center justify-content-center me-3 shadow-sm"
                              style={{ width: '28px', height: '28px', borderRadius: '8px', backgroundColor: selectedFolder === f ? '#4c6ef5' : '#f1f3f5', transition: 'all 0.2s ease' }}
                            >
                              <i className={`fas ${selectedFolder === f ? 'fa-folder-open text-white' : 'fa-folder text-secondary'}`} style={{ fontSize: '0.8rem' }} />
                            </div>
                            <span style={{ color: selectedFolder === f ? '#4c6ef5' : '#495057' }}>{f}</span>
                          </div>
                          {selectedFolder === f && (
                            <div className="ms-2">
                              <i className="fas fa-check" style={{ color: '#4c6ef5', fontSize: '0.9rem' }} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer border-top py-3 px-4 d-flex justify-content-end" style={{ backgroundColor: '#f1f3f5', borderColor: '#e9ecef', gap: '3px' }}>
              <button
                type="button"
                className="btn btn-secondary border-0 btn-sm fw-bold shadow-sm"
                style={{ backgroundColor: '#707077', borderRadius: '6px', fontSize: '0.8rem', padding: '6px 16px' }}
                onClick={() => setShowFolderModal(false)}
              >
                취소
              </button>
              {folders.length > 0 && (
                <button
                  type="button"
                  className="btn btn-primary border-0 btn-sm fw-bold shadow-sm d-flex align-items-center gap-1"
                  style={{ backgroundColor: '#4c6ef5', borderRadius: '6px', fontSize: '0.8rem', padding: '6px 16px' }}
                  onClick={handleMoveFolderSubmit}
                >
                  <i className="fas fa-check" style={{ fontSize: '0.75rem' }} /> 저장
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────── */}
      {/* [모달 2] 폴더명 변경 모달 (PMS 100% 동일) */}
      {/* ──────────────────────────────────────────────────────── */}
      {editFolderModalVisible && (
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1050 }}
        >
          <div className="bg-white rounded-3 shadow-lg" style={{ width: '400px', maxWidth: '90%' }}>
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom rounded-top" style={{ backgroundColor: '#f1f3f5' }}>
              <h6 className="m-0 fw-bold d-flex align-items-center text-dark">
                <i className="far fa-edit me-2" style={{ color: '#4c6ef5', fontSize: '1.2rem', fontWeight: 'bold' }} />폴더명 변경
              </h6>
              <button type="button" className="btn-close" onClick={() => setEditFolderModalVisible(false)} />
            </div>
            <div className="p-4 bg-white">
              <label className="form-label text-muted fs-7 fw-bold mb-2">변경할 폴더명</label>
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0">
                  <i className="fas fa-folder" style={{ color: '#4c6ef5' }} />
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0 shadow-none"
                  value={newEditFolderName}
                  onChange={(e) => setNewEditFolderName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleEditFolderSubmit(); }}
                />
              </div>
            </div>
            <div className="p-3 border-top d-flex justify-content-end gap-2 rounded-bottom" style={{ backgroundColor: '#f1f3f5' }}>
              <button
                type="button"
                className="btn border-0 text-white fw-bold shadow-sm"
                style={{ backgroundColor: '#6c757d', borderRadius: '4px', padding: '6px 18px', fontSize: '0.85rem' }}
                onClick={() => setEditFolderModalVisible(false)}
              >
                취소
              </button>
              <button
                type="button"
                className="btn border-0 text-white fw-bold shadow-sm d-flex align-items-center gap-1"
                style={{ backgroundColor: '#4263eb', borderRadius: '4px', padding: '6px 18px', fontSize: '0.85rem' }}
                onClick={handleEditFolderSubmit}
              >
                <i className="fas fa-check" /> 변경
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────── */}
      {/* [모달 3] 폴더 삭제 모달 (PMS 100% 동일) */}
      {/* ──────────────────────────────────────────────────────── */}
      {deleteFolderModalVisible && (() => {
        const hasFiles = comments.some(c => c.folder_name === selectedDeleteFolder);

        return (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1050 }}
          >
            <div className="bg-white rounded-3 shadow-lg" style={{ width: '400px', maxWidth: '90%' }}>
              <div className="d-flex justify-content-between align-items-center p-3 border-bottom rounded-top" style={{ backgroundColor: '#f1f3f5' }}>
                <h5 className="mb-0 fs-6 fw-bold text-dark">
                  <i className="fas fa-trash-alt text-danger me-2" /> 폴더 삭제
                </h5>
                <button type="button" className="btn-close shadow-none" onClick={() => setDeleteFolderModalVisible(false)} />
              </div>

              <div className="p-4 text-center">
                <p className="mb-3 text-dark fw-bold fs-6">
                  <span className="text-danger">[{selectedDeleteFolder}]</span> 폴더를 삭제하시겠습니까?
                </p>
                {hasFiles ? (
                  <>
                    <p className="text-muted fs-8 mb-4">
                      폴더만 삭제하거나 폴더 안의 파일들까지 한 번에 영구 삭제할 수 있습니다.
                    </p>
                    <div className="d-grid gap-2">
                      <button
                        type="button"
                        className="btn btn-outline-danger fw-bold py-2 shadow-sm"
                        onClick={() => handleDeleteFolderSubmit('folder_only')}
                      >
                        폴더만 삭제 (내부 파일은 유지)
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger fw-bold py-2 shadow-sm"
                        onClick={() => handleDeleteFolderSubmit('folder_and_files')}
                      >
                        폴더와 내부 파일 모두 영구 삭제
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-muted fs-8 mb-4">
                      현재 이 폴더는 비어 있습니다.<br />폴더를 삭제하시겠습니까?
                    </p>
                    <div className="d-grid gap-2">
                      <button
                        type="button"
                        className="btn btn-danger fw-bold py-2 shadow-sm"
                        onClick={() => handleDeleteFolderSubmit('folder_only')}
                      >
                        폴더 삭제
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default WorkOrderFilesTab;
